import { SiloArtCanvas, useSiloArt } from "@/components/SiloArtLayer";
import { ScaledGroup } from "@/components/ScaledGroup";
import { normalizeZones } from "@/lib/siloArt";
// Phase 3 — the private AI Assistant page (the cube's left face).
//
// This is NOT a copy of the Public Q&A. It is the existing hybrid AI Assistant
// (the AiBuddy: full open-world LLM that has ALSO digested the silo's Tier 1 —
// "Full Tier 1" pure infusion + "Smart snippets" lighter review — with the
// voice lanes and the LLM-selector pop-up). Here that SAME feature is simply
// EXPANDED to fill its own full page: its "Your Assistant" header is dropped,
// its controls sit at the top, and the hero copy lives inside its empty
// response area (replacing the old "Ask anything…" line). Private / owner-only.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Sparkle, BookOpenText, X, BookmarkSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser } from "@/lib/api";
import AiBuddy from "@/components/AiBuddy";
import SiloHeaderNav from "@/components/SiloHeaderNav";
import { useTabsOff, registerHiddenTabs, useOpenTabListener } from "@/lib/tabToggle";
import IdentityPill from "@/components/IdentityPill";
import AssistantBookmarks from "@/components/AssistantBookmarks";
import AssistantLengthPanel from "@/components/AssistantLengthPanel";
import AssistantPhotosPanel from "@/components/AssistantPhotosPanel";
import { VOICE_OPTIONS, chunkForSpeech } from "@/lib/useVoiceQA";

// Default hero copy. Phase 3's Bump Studio section will make these editable;
// this page will then read them from config with these as the fallback.
const HERO = {
  eyebrow: "Open-world AI + your whole silo",
  headline: "Your AI Assistant — your front door to the world's AI.",
  sub: "Optionally fold in your whole silo for grounded, on-topic answers.",
};

// Phase 2 edge tabs. LEFT: Bookmarks · Tools · Length. RIGHT: Public QA · Photos.
// (Vertical positions become configurable via the new Bump Studio section in Phase 3.)
const LEFT_TABS = [
  { key: "bookmarks", label: "Bookmarks", top: "120px" },
  { key: "tools", label: "Tools", top: "240px" },
  { key: "length", label: "Length", top: "324px" },
];
const RIGHT_TABS = [
  { key: "public-qa", label: "Public QA", top: "120px", dark: true },
  { key: "photos", label: "Photos", top: "248px", dark: true },
];

function EdgeTab({ sideKey, top, label, active, dark, onClick, testid }) {
  const isLeft = sideKey === "left";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{ top, [isLeft ? "left" : "right"]: 0 }}
      className={`fixed z-50 flex items-center justify-center w-6 py-3 border hairline shadow-sm transition-colors
        ${isLeft ? "rounded-r-lg border-l-0" : "rounded-l-lg border-r-0"}
        ${dark ? "bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#2A2928]" : "bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]"}
        ${active ? "ring-2 ring-[#C16E5A]" : ""}`}
    >
      <span className="text-[10px] uppercase tracking-[0.18em] font-semibold whitespace-nowrap"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
        {label}
      </span>
    </button>
  );
}

export default function SiloAssistant() {
  const { slug } = useParams();
  const nav = useNavigate();
  const me = getUser();
  const [silo, setSilo] = useState(null);
  const [stats, setStats] = useState({ document_count: 0, total_characters: 0 });
  const [est, setEst] = useState({ tier1_doc_count: 0, tier1_chars: 0 });
  const [panel, setPanel] = useState(null); // null | public-qa | photos | bookmarks | tools | length
  // Jun 2026 (Randy) — the assistant's chosen answer length. Shipped on every
  // /buddy/chat call so it holds even when the LLM model changes.
  const [answerStyle, setAnswerStyle] = useState({ preset: null, custom: null });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  // Bookmark wiring (Randy, Jun 2026): live conversation, load/replay, save trigger, share.
  const [convoMessages, setConvoMessages] = useState([]);
  const [loadReq, setLoadReq] = useState(null);     // { nonce, turns }
  const [bookmarkReq, setBookmarkReq] = useState(0); // nonce bumped to open the name-it modal
  const [bmRefresh, setBmRefresh] = useState(0);
  const [clearReq, setClearReq] = useState(0);
  const pageRef = useRef(null);
  // Jul 2026 (Randy) — "Turn off tabs": hide the edge tabs and expose them via
  // the hamburger selector instead (registry consumed by SiloHeaderNav).
  const tabsOff = useTabsOff();
  useEffect(() => registerHiddenTabs(
    "assistant-tabs",
    [...LEFT_TABS, ...RIGHT_TABS].map((t) => ({ key: `assistant-${t.key}`, label: t.label }))
  ), []);
  useOpenTabListener((key) => {
    if ((key || "").startsWith("assistant-")) setPanel(key.slice("assistant-".length));
  });

  // iOS full-height chat layout (rebuilt clean — Jun 2026). Replaces ~5 prior
  // attempts that JS-measured the viewport (visualViewport height + timers),
  // which fought the keyboard and left/created white gaps. NOW: the shell is
  // sized with plain 100dvh (dynamic viewport — it shrinks for BOTH the Safari
  // toolbar and the on-screen keyboard) + interactive-widget=resizes-content
  // (set in index.html). The command box is a flex shrink-0 child, so it stays
  // flush at the bottom of the visible area with NO JS measuring and NO gap.
  // The ONLY thing we do here is lock document scroll while this page is
  // mounted, so the page itself can never be swiped to reveal empty space.
  useEffect(() => {
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevOverscroll = document.body.style.overscrollBehavior;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.overscrollBehavior = prevOverscroll;
    };
  }, []);

  // Keyboard handling (Randy, Jun 2026): on iPhones where the CSS
  // interactive-widget flag isn't honored, iOS OVERLAYS the keyboard and pans
  // the page — leaving a white gap above the keyboard and shoving the toolkit /
  // listen pill off-screen. While the keyboard is open we pin the whole shell
  // to the keyboard's reported visible area (height + offset), so the command
  // box rests right on the keyboard and the top stays put. When it's closed we
  // hand back to the plain CSS 100dvh (proven gap-free on load) by clearing the
  // inline overrides. The "h-[100dvh]" class is the default; inline wins only
  // when the keyboard is up.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const apply = () => {
      const el = pageRef.current;
      if (!el) return;
      const keyboard = window.innerHeight - vv.height - vv.offsetTop;
      if (keyboard > 120) {
        el.style.height = `${Math.round(vv.height)}px`;
        el.style.transform = `translateY(${Math.round(vv.offsetTop)}px)`;
      } else {
        el.style.height = "";
        el.style.transform = "";
      }
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
      const el = pageRef.current;
      if (el) { el.style.height = ""; el.style.transform = ""; }
    };
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get(`/s/${slug}`),
          api.get(`/s/${slug}/stats`).catch(() => ({ data: { document_count: 0, total_characters: 0 } })),
        ]);
        if (cancel) return;
        const isOwner = me && (s.data?.owner_user_id === me.id || me.role === "super_admin");
        if (!isOwner) { setDenied(true); return; }
        setSilo(s.data);
        setStats(st.data || { document_count: 0, total_characters: 0 });
        // The assistant's ACTUAL connected size (its Tier 1) — distinct from the
        // full silo corpus above. Best-effort.
        api.get(`/silos/${s.data.id}/buddy/context-estimate`)
          .then((r) => { if (!cancel) setEst(r.data || {}); })
          .catch(() => {});
      } catch (e) {
        if (e.response?.status === 401) { nav("/login"); return; }
        toast.error("Couldn't load this silo's assistant.");
        setDenied(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Jun 2026 (Randy) — "Open in AI Assistant" deep-link from the Chats tab:
  // /silo/:slug/assistant?session=<id> loads that conversation back into the chat.
  useEffect(() => {
    if (!silo?.id) return;
    const sid = new URLSearchParams(window.location.search).get("session");
    if (!sid) return;
    (async () => {
      try {
        const r = await api.get(`/silos/${silo.id}/assistant-sessions/${encodeURIComponent(sid)}`);
        const turns = (r.data?.messages || []).map((m) => ({ role: m.role === "user" ? "user" : "ai", text: m.text }));
        // A "bookmark::<id>" row is a SAVED (cleared) conversation — load it
        // display-only WITH its bookmark so its 7-day audio greens (no fake session).
        if (r.data?.is_bookmark) {
          setLoadReq({ nonce: Date.now(), turns, bookmark: r.data.bookmark });
        } else {
          setLoadReq({ nonce: Date.now(), turns, session_id: sid });
        }
        nav(`/silo/${slug}/assistant`, { replace: true });
      } catch { /* */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo?.id]);

  // Jul 2026 (Randy) — owner canvas controls. Hook must live ABOVE the
  // early returns to keep hook order stable.
  const { leaderSilo: fxLeader } = useSiloArt();
  const asFx = (normalizeZones(fxLeader?.silo_art).page_fx || {}).assistant || {};
  const asColorCls = asFx.text_color === "black" ? "siloart-text-black" : asFx.text_color === "white" ? "siloart-text-white" : "";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#6B6A68] text-sm" data-testid="silo-assistant-loading">
        Loading your assistant…
      </div>
    );
  }

  if (denied || !silo) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="silo-assistant-denied">
        <p className="font-serif-display text-2xl text-[#1C1B1A]">This assistant is private.</p>
        <p className="text-sm text-[#6B6A68]">Only the silo's owner can open it.</p>
        <button onClick={() => nav("/dashboard")} data-testid="assistant-back-dashboard"
          className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E]">
          Back to dashboard
        </button>
      </div>
    );
  }

  // The hero copy now lives INSIDE the assistant's empty response area.
  // Whole group resizable, forced black/white, or hidden entirely.
  const hero = (ctx) => asFx.text_hidden ? null : (
    <ScaledGroup scale={typeof asFx.text_scale === "number" ? asFx.text_scale / 100 : 1}
      className={`siloart-copy text-center max-w-xl mx-auto ${asColorCls}`} data-testid="assistant-hero">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#6B6A68] mb-5" data-testid="assistant-eyebrow">
        <BookOpenText size={16} weight="duotone" /> {HERO.eyebrow}
      </div>
      <h1 className="font-serif-display text-3xl sm:text-4xl leading-tight text-[#1C1B1A]" data-testid="assistant-headline">
        {HERO.headline}
      </h1>
      <p className="mt-5 text-xs uppercase tracking-[0.25em] text-[#6B6A68] leading-relaxed" data-testid="assistant-sub">
        {HERO.sub}
      </p>
      {ctx === "full" && (
        <div className="mt-7 text-sm text-[#6B6A68]" data-testid="assistant-stats-tier1">
          {(est.tier1_doc_count || 0)} doc{(est.tier1_doc_count || 0) === 1 ? "" : "s"} · {(est.tier1_chars || 0).toLocaleString()} chars in this assistant's knowledge
        </div>
      )}
      <div className="mt-2 text-sm text-[#6B6A68]" data-testid="assistant-infinite">
        <span aria-label="infinite">∞</span> LLM
      </div>
    </ScaledGroup>
  );

  const rightOpen = panel === "public-qa" || panel === "photos";
  const leftOpen = panel === "bookmarks" || panel === "tools" || panel === "length";
  const PANEL_TITLES = { "public-qa": "Public Q&A", photos: "Photos", bookmarks: "Bookmarks", tools: "Tools", length: "Answer length" };
  const renderPanelChrome = (p) => (
    <div className="flex items-center justify-between px-4 py-3 border-b hairline shrink-0 bg-white">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] font-semibold">{PANEL_TITLES[p]}</div>
      <button onClick={() => setPanel(null)} data-testid="assistant-panel-close"
        className="text-[#6B6A68] hover:text-[#C16E5A] w-7 h-7 flex items-center justify-center rounded-md border hairline">
        <X size={14} weight="bold" />
      </button>
    </div>
  );
  const renderPanelBody = (p) => {
    if (p === "public-qa") {
      return (
        <iframe key="pqa" title="Public Q&A" src={`${window.location.origin}/s/${silo.slug}`}
          className="w-full h-full border-0" data-testid="assistant-public-qa-iframe" />
      );
    }
    if (p === "photos") {
      return <AssistantPhotosPanel silo={silo} />;
    }
    if (p === "bookmarks") {
      return (
        <AssistantBookmarks
          silo={silo}
          refreshKey={bmRefresh}
          onLoad={(b) => { setLoadReq({ nonce: Date.now(), turns: b.messages || [], bookmark: b }); setPanel(null); }}
        />
      );
    }
    if (p === "tools") {
      const hasConvo = convoMessages.some((m) => (m.text || "").trim());
      return (
        <div className="p-4 space-y-2" data-testid="assistant-tools-panel">
          <button
            type="button"
            data-testid="assistant-tool-bookmark"
            disabled={!hasConvo}
            onClick={() => { setBookmarkReq((n) => n + 1); setPanel(null); }}
            className="w-full flex items-start gap-3 p-3 rounded-lg border hairline bg-white text-left hover:border-[#4A5D4E] disabled:opacity-50 disabled:hover:border-[#E5E5E0]"
          >
            <BookmarkSimple size={18} weight="duotone" className="mt-0.5 text-[#4A5D4E] shrink-0" />
            <div>
              <div className="text-sm font-semibold text-[#1C1B1A]">Bookmark this conversation</div>
              <div className="text-xs text-[#6B6A68]">{hasConvo ? "Saves the chat + its cached audio (kept 7 days)." : "Ask something first."}</div>
            </div>
          </button>
        </div>
      );
    }
    if (p === "length") {
      return (
        <AssistantLengthPanel
          silo={silo}
          activePreset={answerStyle.preset}
          activeCustom={answerStyle.custom}
          onChange={({ preset, custom }) => setAnswerStyle({ preset, custom })}
        />
      );
    }
    const labels = {
      length: "Answer-length controls will live here.",
    };
    return (
      <div className="h-full flex items-center justify-center text-sm text-[#9b9a96] p-8 text-center" data-testid={`assistant-${p}-scaffold`}>
        {labels[p]}
      </div>
    );
  };


  // Recall (Jun 2026, Randy) — reopen the owner's recent Assistant conversations
  // on this silo (any device). Loading one refreshes its 30-day text clock + 24h
  // audio cache. Loads via AiBuddy's loadRequest (carrying the real session_id).
  const recallSurfaceKey = `silo-assistant::${silo.id}`;
  const recallProps = {
    enabled: true,
    testidPrefix: "assistant-recall",
    fetchRecent: async () => {
      try {
        const r = await api.get(`/buddy/recent-sessions?surface=${encodeURIComponent(recallSurfaceKey)}&limit=5`);
        return r.data?.sessions || [];
      } catch { return []; }
    },
    onLoad: (sess) => {
      if (sess.is_bookmark) {
        setLoadReq({ nonce: Date.now(), turns: sess.messages || [], bookmark: sess.bookmark });
      } else {
        setLoadReq({ nonce: Date.now(), turns: sess.messages || [], session_id: sess.session_id });
      }
      setPanel(null);
    },
    touch: (sess) => {
      const av = "onyx";
      const i = Math.max(0, VOICE_OPTIONS.findIndex((o) => o.id === av));
      const qv = VOICE_OPTIONS[(i + 1) % VOICE_OPTIONS.length].id;
      const segs = (sess.messages || [])
        .filter((m) => (m.text || "").trim())
        .map((m) => ({ voice: (m.role === "user" ? qv : av), chunks: chunkForSpeech(m.text) }));
      api.post(`/buddy/recall-touch`, { session_id: sess.session_id, segments: segs }).catch(() => {});
    },
  };

  return (
    <div ref={pageRef} data-template={silo.template} className="fixed inset-x-0 top-0 h-[100dvh] flex flex-col overflow-hidden isolate" style={{ minHeight: 0, background: "var(--tpl-bg, #FDFCF8)" }} data-testid="silo-assistant-page">
      <SiloArtCanvas pageKey="assistant" />
      {/* Top bar — reuses the Public Q&A's shared hamburger/Home nav + IdentityPill
          (no Star). Changes to SiloHeaderNav propagate to both pages. */}
      <header className="sticky top-0 z-40 shrink-0 border-b tpl-border backdrop-blur-md" style={{ background: "color-mix(in srgb, var(--tpl-bg, #FDFCF8) 92%, transparent)" }}>
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between gap-3">
          <SiloHeaderNav onHome={() => nav("/dashboard")} homeTestid="assistant-home-btn" recall={recallProps} tabsToggle />
          <Link to="/dashboard" className="flex items-center gap-3 min-w-0 flex-1" data-testid="assistant-logo-home">
            <div className="w-9 h-9 rounded-full tpl-accent-bg flex items-center justify-center shrink-0">
              <Sparkle size={18} weight="duotone" />
            </div>
            <div className="min-w-0">
              <div className="tpl-display text-xl leading-none truncate">{silo.name}</div>
              <div className="text-[10px] tpl-muted tracking-[0.25em] uppercase mt-1 truncate">Private assistant</div>
            </div>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setClearReq((n) => n + 1)}
              disabled={!convoMessages.some((m) => (m.text || "").trim())}
              data-testid="assistant-clear-btn"
              title="Clear this conversation"
              className="text-[11px] tpl-muted hover:tpl-accent flex items-center gap-1 px-2 py-1 rounded-md border tpl-border tpl-surface shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash size={12} weight="duotone" /> Clear
            </button>
            <IdentityPill siloId={silo?.id} siloSlug={silo?.slug} />
          </div>
        </div>
      </header>

      {/* Edge tabs (Phase 2). LEFT: Bookmarks · Tools · Length. RIGHT: Public QA · Photos.
          Hidden entirely when "Turn off tabs" is on — the hamburger lists them instead. */}
      {!tabsOff && LEFT_TABS.map((t) => (
        <EdgeTab key={t.key} sideKey="left" top={t.top} label={t.label}
          active={panel === t.key} onClick={() => setPanel((p) => (p === t.key ? null : t.key))}
          testid={`assistant-tab-${t.key}`} />
      ))}
      {!tabsOff && RIGHT_TABS.map((t) => (
        <EdgeTab key={t.key} sideKey="right" top={t.top} label={t.label} dark={t.dark}
          active={panel === t.key} onClick={() => setPanel((p) => (p === t.key ? null : t.key))}
          testid={`assistant-tab-${t.key}`} />
      ))}

      {/* Main = the expanded assistant, full-screen. Right tabs split the screen on desktop. */}
      <div className="flex items-stretch overflow-hidden flex-1 min-h-0">
        <main className={`siloart-copy ${rightOpen ? "w-full lg:w-1/2" : "w-full"} min-w-0 flex flex-col`} data-testid="silo-assistant-engine">
          <AiBuddy
            surface="silo-assistant"
            siloId={silo.id}
            siloSlug={silo.slug}
            siloName={silo.name}
            showSiloControls
            hideHeader
            fullHeight
            startFresh
            emptyState={hero}
            inputPlaceholder="Ask your hybrid assistant anything…"
            onMessagesChange={setConvoMessages}
            answerStyle={answerStyle}
            onSendToWorkbench={async ({ question, answer }) => {
              try {
                await api.post(`/silos/${silo.id}/send-to-staging`, {
                  messages: [
                    ...(question ? [{ role: "user", text: question }] : []),
                    { role: "assistant", text: answer },
                  ],
                });
                toast.success("Sent to your Workbench staging.");
              } catch {
                toast.error("Couldn't send to the Workbench. Try again.");
                throw new Error("send-to-staging failed");
              }
            }}
            onJumpToWorkbench={() => {
              try { sessionStorage.setItem(`silo_tab:${silo.id}`, "workbench"); } catch { /* */ }
              nav(`/dashboard/silo/${silo.id}`);
            }}
            onSendAllToWorkbench={async (messages) => {
              try {
                await api.post(`/silos/${silo.id}/send-to-staging`, { messages });
                toast.success("Whole conversation sent to your Workbench staging.");
              } catch {
                toast.error("Couldn't send to the Workbench. Try again.");
                throw new Error("send-all-to-staging failed");
              }
            }}
            onDeleteTurn={async ({ session_id, bookmark_id, question, answer }) => {
              await api.post(`/silos/${silo.id}/assistant/delete-turn`, { session_id, bookmark_id, question, answer });
            }}
            clearRequest={clearReq}
            loadRequest={loadReq}
            bookmarkRequest={bookmarkReq}
            onBookmarkSaved={() => setBmRefresh((n) => n + 1)}
          />
        </main>
        {/* DESKTOP right panel — true split screen (assistant left, Public QA right) */}
        {rightOpen && (
          <aside className="hidden lg:flex lg:flex-col w-1/2 border-l hairline bg-white h-full" data-testid="assistant-right-panel">
            {renderPanelChrome(panel)}
            <div className="flex-1 min-h-0">{renderPanelBody(panel)}</div>
          </aside>
        )}
      </div>

      {/* MOBILE right drawer (uses current bump-style slide-out width) */}
      {rightOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="assistant-right-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPanel(null)} />
          <div className="absolute inset-y-0 right-0 w-[92vw] max-w-md bg-white flex flex-col shadow-2xl">
            {renderPanelChrome(panel)}
            <div className="flex-1 min-h-0 overflow-y-auto cta-safe">{renderPanelBody(panel)}</div>
          </div>
        </div>
      )}

      {/* LEFT drawer — Bookmarks / Tools / Length */}
      {leftOpen && (
        <div className="fixed inset-0 z-40" data-testid="assistant-left-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPanel(null)} />
          <div className="absolute inset-y-0 left-0 w-[88vw] max-w-sm bg-white flex flex-col shadow-2xl">
            {renderPanelChrome(panel)}
            <div className="flex-1 min-h-0 overflow-y-auto">{renderPanelBody(panel)}</div>
          </div>
        </div>
      )}

    </div>
  );
}
